import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCheck,
  faFile,
  faCheckCircle,
  faSpinner,
  faSearch,
  faBuilding,
  faGlobe,
  faUser,
  faUsers,
  faBriefcase,
  faLayerGroup,
  faUpload,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import ClientTabs from "../Clients/ClientTabs";

const AssignTemplateToAgent = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // ─── State for Agents ──────────────────────────────────────────────
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentSearchTerm, setAgentSearchTerm] = useState("");

  // ─── State for Templates ──────────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templateSearchTerm, setTemplateSearchTerm] = useState("");
  const [templateTypeFilter, setTemplateTypeFilter] = useState("B2C");

  // ─── Selection States ─────────────────────────────────────────────
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [domainName, setDomainName] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");

  // ─── Assignment State ─────────────────────────────────────────────
  const [assigning, setAssigning] = useState(false);
  const [assignmentSuccess, setAssignmentSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // ─── Existing Assignments ─────────────────────────────────────────
  const [existingAssignments, setExistingAssignments] = useState({});
  
  // ─── Additional JSON States for Template Customization ──────────
  const [customBrandingJson, setCustomBrandingJson] = useState("");
  const [styleJson, setStyleJson] = useState("");
  const [topBarJson, setTopBarJson] = useState("");
  const [destinationsJson, setDestinationsJson] = useState("");
  const [heroJson, setHeroJson] = useState("");
  const [headerJson, setHeaderJson] = useState("");
  const [footerJson, setFooterJson] = useState("");
  const [aboutUsJson, setAboutUsJson] = useState("");
  const [contactUsJson, setContactUsJson] = useState("");
  const [faqJson, setFaqJson] = useState("");
  const [privacyPolicyJson, setPrivacyPolicyJson] = useState("");
  const [termsConditionsJson, setTermsConditionsJson] = useState("");
  const [refundPolicyJson, setRefundPolicyJson] = useState("");
  const [seoJson, setSeoJson] = useState("");
  const [companyDataJson, setCompanyDataJson] = useState("");

  // ─── Template Type Options ────────────────────────────────────────
  const templateTypeOptions = [
    { value: "B2C", label: "B2C" },
    { value: "Travel", label: "Travel" },
    { value: "B2E", label: "B2E / Corporate" },
    { value: "B2B", label: "B2B" },
  ];

  // ─── Auth Guard ──────────────────────────────────────────────────
  useEffect(() => {
    if (!loading && !user) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  // ─── Fetch All Agents (Clients) ──────────────────────────────────
  const fetchAgents = async () => {
    if (!user?.cid) return;
    setAgentsLoading(true);
    try {
      const response = await apiService.getWithParams(
        `${config.GETCOMPANIES}?companyType=CLIENT&PageNumber=1&PageSize=100`
      );
      const agentsData = response.data || [];
      setAgents(agentsData);
      setFilteredAgents(agentsData);
    } catch (error) {
      console.error("Error fetching agents:", error);
      setAgents([]);
      setFilteredAgents([]);
    } finally {
      setAgentsLoading(false);
    }
  };

  // ─── Fetch Templates Based on Type ───────────────────────────────
  const fetchTemplates = async (type = "B2C") => {
    if (!user?.cid) return;
    setTemplatesLoading(true);
    try {
      const response = await apiService.getAll(
        `${config.GETALLWEBSITETEMPLATES}?type=${type}`
      );
      const allData = response.data?.items || response.data || response || [];

      const formattedTemplates = allData.map((t) => ({
        id: t.templateId,
        title: t.name || t.template || "Untitled",
        description: t.description || "",
        type: t.type || type,
        thumbnail: t.thumbnailUrl || "",
        template: t.template || "",
        isActive: t.isActive !== false,
        templateReference: t.template || "",
      }));

      setTemplates(formattedTemplates);
      setFilteredTemplates(formattedTemplates);
    } catch (error) {
      console.error(`Error fetching ${type} templates:`, error);
      setTemplates([]);
      setFilteredTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  // ─── Fetch Existing Assignments ──────────────────────────────────
  const fetchExistingAssignments = async () => {
    if (!user?.cid) return;
    try {
      const res = await apiService.getAll(
        `${config.GETTEMPLATEBYCOMPANYID}/${user.cid}`
      );
      const assignments = Array.isArray(res) ? res : res.data || [];
      const assignmentMap = {};
      assignments.forEach((item) => {
        if (item.templateId) {
          assignmentMap[item.templateId] = {
            companyId: item.companyId || item.companyID,
            domain: item.domain || "",
            assignedAt: item.assignedAt || new Date().toISOString(),
          };
        }
      });
      setExistingAssignments(assignmentMap);
    } catch (error) {
      console.error("Error fetching assignments:", error);
      setExistingAssignments({});
    }
  };

  // ─── Initial Data Load ────────────────────────────────────────────
  useEffect(() => {
    if (user && !loading) {
      fetchAgents();
      fetchTemplates("B2C");
      fetchExistingAssignments();
    }
  }, [user, loading]);

  // ─── Handle Type Filter Change ────────────────────────────────────
  const handleTypeFilterChange = (e) => {
    const selectedType = e.target.value;
    setTemplateTypeFilter(selectedType);
    fetchTemplates(selectedType);
    setSelectedTemplate(null);
    setDomainName("");
    setTemplateSearchTerm("");
    resetCustomizationData();
  };

  // ─── Reset Customization Data ────────────────────────────────────
  const resetCustomizationData = () => {
    setCustomBrandingJson("");
    setStyleJson("");
    setTopBarJson("");
    setDestinationsJson("");
    setHeroJson("");
    setHeaderJson("");
    setFooterJson("");
    setAboutUsJson("");
    setContactUsJson("");
    setFaqJson("");
    setPrivacyPolicyJson("");
    setTermsConditionsJson("");
    setRefundPolicyJson("");
    setSeoJson("");
    setCompanyDataJson("");
    setLogoFile(null);
    setLogoPreview("");
  };

  // ─── Filter Agents by Search ──────────────────────────────────────
  useEffect(() => {
    if (agentSearchTerm.trim() === "") {
      setFilteredAgents(agents);
    } else {
      const searchLower = agentSearchTerm.toLowerCase();
      const filtered = agents.filter(
        (agent) =>
          agent.companyName?.toLowerCase().includes(searchLower) ||
          agent.brandName?.toLowerCase().includes(searchLower) ||
          agent.email?.toLowerCase().includes(searchLower)
      );
      setFilteredAgents(filtered);
    }
  }, [agentSearchTerm, agents]);

  // ─── Filter Templates by Search ──────────────────────────────────
  useEffect(() => {
    let filtered = templates;
    
    if (templateSearchTerm.trim() !== "") {
      const searchLower = templateSearchTerm.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.title?.toLowerCase().includes(searchLower) ||
          t.description?.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredTemplates(filtered);
  }, [templateSearchTerm, templates]);

  // ─── Handle Template Selection ────────────────────────────────────
  const handleTemplateSelect = (template) => {
    if (selectedTemplate?.id === template.id) {
      setSelectedTemplate(null);
      setDomainName("");
      resetCustomizationData();
    } else {
      setSelectedTemplate(template);
      
      // Pre-fill domain if already assigned
      const existing = existingAssignments[template.id];
      if (existing) {
        setDomainName(existing.domain || "");
      } else if (selectedAgent) {
        const defaultDomain = `${selectedAgent.brandName || selectedAgent.companyName || "agent"}-${template.template || "site"}.zobookai.com`;
        setDomainName(defaultDomain.toLowerCase().replace(/\s+/g, "-"));
      }
      
      // Initialize customization JSONs with default values
      initializeCustomizationData(template);
    }
    setAssignmentSuccess(false);
    setErrorMessage("");
  };

  // ─── Initialize Customization Data ───────────────────────────────
  const initializeCustomizationData = (template) => {
    const agent = selectedAgent;
    const logoUrl = agent?.logoUrl || "";
    const companyName = agent?.companyName || "";
    const brandName = agent?.brandName || "";
    const email = agent?.email || "";
    const phoneNumber = agent?.phoneNumber || "";

    // About Us JSON - Rich structure with hero, story, stats, values, features, team, gallery
    const aboutUsData = {
      heroTitle: `About ${brandName || companyName || "Our Agency"}`,
      heroSubtitle: `Trusted by thousands of travelers for seamless journeys, exceptional service, and unforgettable travel experiences worldwide.`,
      heroBgImage: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1600&q=80",
      storyLabel: "OUR STORY",
      title: `Creating Memorable Journeys Across the World`,
      description: `${brandName || companyName} was founded with a passion for travel and a commitment to making every journey extraordinary. Over the years, we have helped thousands of travelers explore new destinations, discover different cultures, and create lifelong memories through carefully planned travel experiences.`,
      description2: "From international vacations and family holidays to business travel, visa assistance, and customized tour packages, our dedicated team ensures every detail is handled with professionalism and care. We believe travel should be enjoyable, stress-free, and accessible to everyone.",
      image: "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg",
      stats: [
        { v: "10+", l: "Years Experience" },
        { v: "50K+", l: "Happy Travelers" },
        { v: "100+", l: "Destinations Covered" },
        { v: "24/7", l: "Customer Support" }
      ],
      valuesLabel: "OUR VALUES",
      valuesTitle: "What We Stand For",
      values: [
        { icon: "ri-shield-star-line", t: "Trust & Transparency", d: "We maintain complete transparency in pricing, policies, and travel arrangements." },
        { icon: "ri-heart-line", t: "Customer First", d: "Every travel plan is designed around customer comfort and satisfaction." },
        { icon: "ri-global-line", t: "Global Expertise", d: "Our destination specialists help travelers explore the world with confidence." },
        { icon: "ri-handshake-line", t: "Reliable Service", d: "Professional support before, during, and after every journey." }
      ],
      featuresLabel: "WHAT WE OFFER",
      featuresTitle: "Complete Travel Solutions Under One Roof",
      features: [
        { icon: "ri-flight-takeoff-line", title: "Flight Reservations", description: "Domestic and international flight bookings at competitive prices." },
        { icon: "ri-hotel-line", title: "Hotel Bookings", description: "Handpicked accommodations from budget to luxury." },
        { icon: "ri-map-pin-line", title: "Holiday Packages", description: "Customized holiday packages for every traveler." },
        { icon: "ri-customer-service-line", title: "Visa Assistance", description: "Expert support for visas and travel documentation." }
      ],
      teamLabel: "OUR TEAM",
      teamTitle: "Meet the Experts Behind Your Journey",
      team: [
        { name: "Travel Expert", role: "Flights Specialist", initials: "TE", image: "" },
        { name: "Hotel Curator", role: "Accommodation Expert", initials: "HC", image: "" },
        { name: "Tour Guide", role: "Destination Expert", initials: "TG", image: "" }
      ],
      gallery: [
        { url: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e", caption: "Beautiful Beach Destinations" },
        { url: "https://images.unsplash.com/photo-1516483638261-f4dbaf036963", caption: "International City Tours" }
      ],
      extraSections: [
        { label: "WHY CHOOSE US", title: "Your Trusted Travel Partner", description: "We combine experience, destination expertise, and personalized service to create exceptional travel experiences.", image: "https://images.unsplash.com/photo-1521295121783-8a321d551ad2" }
      ],
      ctaTitle: "Ready To Explore The World?",
      ctaSubtitle: "Let us help you plan your next unforgettable journey.",
      ctaButtonText: "Start Your Journey",
      ctaButtonLink: "/contact-us"
    };
    setAboutUsJson(JSON.stringify(aboutUsData, null, 2));

    // Contact Us JSON - Rich structure with hero, info cards, map, form
    const contactUsData = {
      heroTitle: `Contact ${brandName || companyName || "Our Agency"}`,
      heroSubtitle: "We're here to help you plan unforgettable journeys and answer all your travel questions.",
      heroBgImage: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1600&q=80",
      sectionTitle: "Get in Touch",
      description: "Whether you're planning a family vacation, business trip, honeymoon, pilgrimage, or international holiday, our travel experts are ready to assist you. Contact us through any of the channels below and we'll be happy to help.",
      infoCards: [
        { icon: "ri-phone-line", title: "Customer Support", value: phoneNumber || "+91 0000000000" },
        { icon: "ri-mail-line", title: "Email Address", value: email || "info@example.com" },
        { icon: "ri-map-pin-2-line", title: "Office Address", value: agent?.address || "Your City, Your State, India" },
        { icon: "ri-time-line", title: "Working Hours", value: "Monday - Saturday, 9:00 AM - 8:00 PM" },
        { icon: "ri-whatsapp-line", title: "WhatsApp Support", value: phoneNumber || "+91 0000000000" }
      ],
      showSocials: true,
      mapIframeUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3807.044528120665!2d78.56014669999999!3d17.4096506!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb97477de19ea3%3A0xbf1563c1b0fdc630!2sDevelopers%20Code!5e0!3m2!1sen!2sin!4v1780817864000!5m2!1sen!2sin",
      formTitle: "Send Us a Message",
      formSuccessTitle: "Message Sent Successfully!",
      formSuccessText: `Thank you for contacting ${brandName || companyName || "us"}. Our team will get back to you shortly.`,
      formButtonText: "Send Message",
      extraSections: [
        { label: "CUSTOMER SUPPORT", title: "Dedicated Travel Assistance", description: "Our travel consultants are available to assist with bookings, cancellations, visa guidance, holiday planning, and travel-related inquiries.", image: "https://images.unsplash.com/photo-1556740749-887f6717d7e4" },
        { label: "CORPORATE TRAVEL", title: "Business Travel Solutions", description: "We provide complete corporate travel management including flights, hotels, transfers, and travel support for organizations of all sizes.", image: "https://images.unsplash.com/photo-1520607162513-77705c0f0d4a" }
      ]
    };
    setContactUsJson(JSON.stringify(contactUsData, null, 2));

    // FAQ JSON - Rich structure with hero and FAQs
    const faqData = {
      heroTitle: "Frequently Asked Questions",
      heroSubtitle: "Find answers to the most common questions about our travel services, bookings, payments, and support.",
      heroBgImage: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1600&q=80",
      faqs: [
        { q: "How can I book a flight through " + (brandName || companyName || "us") + "?", a: "You can search for flights on our website, compare fares, select your preferred option, and complete the booking online. Our support team is also available to assist with reservations." },
        { q: "Can I modify or cancel my booking after confirmation?", a: "Yes. Booking modifications and cancellations are subject to airline, hotel, or supplier policies. Applicable charges may apply depending on the booking conditions." },
        { q: "How long does it take to receive a refund?", a: "Refund timelines depend on the airline, hotel, or service provider. Most refunds are processed within 7–21 business days after approval." },
        { q: "Do you provide visa assistance?", a: "Yes. We assist travelers with visa documentation, application guidance, appointment scheduling, and other related services for eligible destinations." },
        { q: "Can I book holiday packages for groups?", a: "Absolutely. We offer customized group packages for families, corporate teams, educational tours, religious pilgrimages, and special events." },
        { q: "What payment methods do you accept?", a: "We accept major credit cards, debit cards, UPI payments, net banking, and other secure online payment methods." },
        { q: "Will I receive a booking confirmation immediately?", a: "Most bookings generate an instant confirmation. In some cases, supplier verification may be required, and our team will keep you informed." },
        { q: "Do you offer travel insurance?", a: "Yes. Travel insurance options are available for domestic and international trips and can be added during the booking process." },
        { q: "Can I contact support outside business hours?", a: "Yes. Emergency travel assistance is available for urgent travel-related issues. You may also leave a message, and our team will respond as soon as possible." },
        { q: "How can I contact " + (brandName || companyName || "you") + "?", a: "You can reach us via phone, email, WhatsApp, or through the Contact Us page on our website." }
      ],
      ctaTitle: "Still Have Questions?",
      ctaText: "Our travel experts are ready to assist you with bookings, travel planning, cancellations, visas, and any other queries.",
      ctaButton: "Contact Support",
      ctaLink: "/contact-us"
    };
    setFaqJson(JSON.stringify(faqData, null, 2));

    // Privacy Policy JSON - Rich structure with hero, sections, and footer
    const privacyPolicyData = {
      heroTitle: "Privacy Policy",
      heroBgImage: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1800&q=50",
      showLastUpdated: true,
      introText: `At ${brandName || companyName || "our company"}, we are committed to protecting your privacy and safeguarding your personal information. This Privacy Policy explains how we collect, use, store, and protect the information you provide when using our website, products, and services.`,
      sections: [
        { t: "1. Information We Collect", b: "We collect personal information such as your name, email address, phone number, passport details, billing information, and travel preferences when you make bookings, submit inquiries, or register on our website." },
        { t: "2. How We Use Your Information", b: "The information we collect is used to process bookings, provide customer support, communicate travel updates, improve our services, personalize your experience, and comply with legal obligations." },
        { t: "3. Sharing of Information", b: "We may share your information with airlines, hotels, transport providers, visa processing agencies, payment gateways, and other trusted partners only when necessary to fulfill your travel requirements." },
        { t: "4. Payment Security", b: "All online payments are processed through secure payment gateways. We do not store complete debit card, credit card, or banking information on our systems." },
        { t: "5. Cookies and Tracking Technologies", b: "Our website may use cookies and similar technologies to enhance user experience, analyze website traffic, remember preferences, and improve website functionality." },
        { t: "6. Data Protection", b: "We implement appropriate technical and organizational measures to protect personal information from unauthorized access, loss, misuse, disclosure, or alteration." },
        { t: "7. Third-Party Links", b: "Our website may contain links to third-party websites. We are not responsible for the privacy practices, content, or security of those external websites." },
        { t: "8. Data Retention", b: "We retain personal information only as long as necessary to provide services, comply with legal requirements, resolve disputes, and enforce our agreements." },
        { t: "9. Your Rights", b: "You may request access, correction, deletion, or restriction of your personal information by contacting our support team, subject to applicable laws and regulations." },
        { t: "10. Changes to this Privacy Policy", b: "We reserve the right to update this Privacy Policy at any time. Updated versions will be published on this page and become effective immediately upon posting." }
      ],
      footerNote: "If you have any questions regarding this Privacy Policy or the handling of your personal information, please",
      footerLinkText: "contact us",
      footerLink: "/contact-us"
    };
    setPrivacyPolicyJson(JSON.stringify(privacyPolicyData, null, 2));

    // Terms & Conditions JSON - Rich structure with hero, sections, and footer
    const termsConditionsData = {
      heroTitle: "Terms & Conditions",
      heroBgImage: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1800&q=50",
      showLastUpdated: true,
      introText: "Please read these Terms and Conditions carefully before using our website and travel services. By accessing, browsing, or booking through our platform, you acknowledge that you have read, understood, and agreed to be bound by these terms.",
      sections: [
        { t: "1. Acceptance of Terms", b: "By accessing and using our website and services, you agree to comply with and be legally bound by these Terms and Conditions. If you do not agree with any part of these terms, you should discontinue the use of our services immediately." },
        { t: "2. Booking Confirmation", b: "All travel bookings are subject to availability and confirmation from airlines, hotels, transport operators, visa authorities, or other service providers. A booking is considered confirmed only after payment has been successfully processed and a confirmation has been issued." },
        { t: "3. Pricing and Payments", b: "Prices displayed on our platform are subject to change without prior notice due to supplier updates, currency fluctuations, taxes, or government regulations. Full payment may be required before travel documents or vouchers are issued." },
        { t: "4. Customer Responsibilities", b: "Travelers are responsible for providing accurate information during booking, including names, passport details, contact information, and travel requirements. Any errors may result in additional charges or booking cancellation." },
        { t: "5. Passport and Visa Requirements", b: "Passengers are solely responsible for obtaining and maintaining valid passports, visas, permits, vaccination certificates, and any other travel documentation required by the destination country." },
        { t: "6. Amendments and Modifications", b: "Requests for changes to confirmed bookings are subject to supplier policies and may incur amendment fees, fare differences, or administrative charges. Not all bookings are eligible for modifications." },
        { t: "7. Cancellation Policy", b: "Cancellation requests will be processed according to the policies of the relevant airline, hotel, tour operator, or service provider. Cancellation charges may apply, and some bookings may be partially or fully non-refundable." },
        { t: "8. Refund Policy", b: "Approved refunds will be processed according to supplier timelines and payment provider requirements. Refund processing periods may vary depending on the booking type and payment method used." },
        { t: "9. Travel Insurance", b: "Travel insurance is strongly recommended for all travelers. Insurance coverage, benefits, and exclusions are governed by the insurance provider and not by our company." },
        { t: "10. Limitation of Liability", b: "We act as an intermediary between travelers and travel service providers. We shall not be liable for delays, cancellations, schedule changes, overbookings, loss of baggage, accidents, natural disasters, government actions, or any circumstances beyond our reasonable control." },
        { t: "11. Force Majeure", b: "We shall not be held responsible for any failure or delay in performing obligations due to events beyond our control, including natural disasters, pandemics, strikes, war, terrorism, civil unrest, weather conditions, or government restrictions." },
        { t: "12. Intellectual Property", b: "All content on this website, including text, graphics, logos, images, designs, and software, is the property of the company or its licensors and is protected by applicable intellectual property laws." },
        { t: "13. Privacy and Data Protection", b: "Use of personal information is governed by our Privacy Policy. By using our services, you consent to the collection, storage, and processing of information as described in the Privacy Policy." },
        { t: "14. Prohibited Activities", b: "Users must not misuse the website, attempt unauthorized access, interfere with security features, distribute malicious software, or engage in fraudulent activities related to bookings or payments." },
        { t: "15. Governing Law", b: "These Terms and Conditions shall be governed and interpreted in accordance with the laws of India. Any disputes arising from the use of our services shall be subject to the exclusive jurisdiction of the competent courts." },
        { t: "16. Changes to Terms", b: "We reserve the right to modify, update, or replace these Terms and Conditions at any time without prior notice. Continued use of our services after changes are posted constitutes acceptance of the revised terms." }
      ],
      footerNote: "For any questions regarding these Terms & Conditions, please",
      footerLinkText: "contact us",
      footerLink: "/contact-us"
    };
    setTermsConditionsJson(JSON.stringify(termsConditionsData, null, 2));

    // Refund Policy JSON - Rich structure with hero, tables, and general rows
    const refundPolicyData = {
      heroTitle: "Refund & Cancellation Policy",
      heroSubtitle: "Transparent policies for a worry-free booking experience",
      heroBgImage: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1800&q=50",
      infoNote: "Refund policies vary depending on the service booked and the supplier's terms and conditions. The information below provides general guidelines. Specific cancellation charges and refund eligibility will be displayed during the booking process.",
      cancelTables: [
        {
          title: "Flight Cancellations",
          icon: "ri-flight-takeoff-line",
          rows: [
            { label: "More than 7 days before departure", value: "Full refund (airline rules apply)", badge: "#dcfce7", color: "#16a34a" },
            { label: "48 hours to 7 days before departure", value: "Partial refund", badge: "#fef3c7", color: "#d97706" },
            { label: "Less than 48 hours before departure", value: "Cancellation charges apply", badge: "#fee2e2", color: "#dc2626" },
            { label: "No-show", value: "Non-refundable", badge: "#fee2e2", color: "#dc2626" }
          ]
        },
        {
          title: "Hotel Cancellations",
          icon: "ri-hotel-line",
          rows: [
            { label: "More than 72 hours before check-in", value: "Full refund", badge: "#dcfce7", color: "#16a34a" },
            { label: "24-72 hours before check-in", value: "Partial refund", badge: "#fef3c7", color: "#d97706" },
            { label: "Less than 24 hours before check-in", value: "Non-refundable", badge: "#fee2e2", color: "#dc2626" }
          ]
        },
        {
          title: "Holiday Package Cancellations",
          icon: "ri-suitcase-line",
          rows: [
            { label: "More than 30 days before travel", value: "90% refund", badge: "#dcfce7", color: "#16a34a" },
            { label: "15-30 days before travel", value: "75% refund", badge: "#fef3c7", color: "#d97706" },
            { label: "7-14 days before travel", value: "50% refund", badge: "#fef3c7", color: "#d97706" },
            { label: "Less than 7 days before travel", value: "Non-refundable", badge: "#fee2e2", color: "#dc2626" }
          ]
        },
        {
          title: "Visa Services",
          icon: "ri-passport-line",
          rows: [
            { label: "Before application submission", value: "Service charges may apply", badge: "#fef3c7", color: "#d97706" },
            { label: "After embassy submission", value: "Non-refundable", badge: "#fee2e2", color: "#dc2626" }
          ]
        }
      ],
      generalRows: [
        { t: "Refund Processing Time", d: "Approved refunds are generally processed within 7 to 21 business days depending on the payment gateway, bank, airline, hotel, or supplier involved." },
        { t: "Service Charges", d: "Platform convenience fees, transaction charges, and service fees may be non-refundable once a booking has been confirmed." },
        { t: "Supplier Policies", d: "Airlines, hotels, transport operators, and other travel suppliers may apply their own cancellation and refund rules in addition to our policies." },
        { t: "Force Majeure Events", d: "Refund eligibility during natural disasters, government restrictions, pandemics, strikes, or other extraordinary events will depend on supplier policies and applicable regulations." },
        { t: "Refund Method", d: "Refunds are usually credited back to the original payment method used during booking unless otherwise agreed in writing." },
        { t: "Booking Amendments", d: "Changes to travel dates, passenger names, or travel services may attract amendment fees and fare differences depending on supplier rules." }
      ],
      ctaText: "Need assistance with a cancellation, amendment, or refund request?",
      ctaButtonLabel: "Contact Support",
      ctaButtonLink: "/contact-us"
    };
    setRefundPolicyJson(JSON.stringify(refundPolicyData, null, 2));

    // Custom Branding JSON
    const brandingData = {
      logoUrl: logoUrl,
      companyName: companyName,
      brandName: brandName,
      email: email,
      phones: [phoneNumber],
      whatsapp: "",
      socials: {
        facebook: "",
        instagram: "",
        x: "",
        linkedin: "",
        youtube: ""
      }
    };
    setCustomBrandingJson(JSON.stringify(brandingData, null, 2));

    // Style JSON
    const styleData = {
      themeColor1: "#007bff",
      themeColor2: "#6c757d",
      themeColor3: "#28a745",
      themeColor4: "#dc3545",
      themeColor5: "#ffffff"
    };
    setStyleJson(JSON.stringify(styleData, null, 2));

    // Footer JSON
    const footerData = {
      aboutText: `Discover the world with ${brandName || companyName || "us"}.`,
      copyright: `© ${new Date().getFullYear()} All Rights Reserved.`
    };
    setFooterJson(JSON.stringify(footerData, null, 2));

    // Hero JSON - Empty slides array
    const heroData = {
      slides: []
    };
    setHeroJson(JSON.stringify(heroData, null, 2));

    // Destinations JSON - Empty destinations array
    const destinationsData = {
      destinations: []
    };
    setDestinationsJson(JSON.stringify(destinationsData, null, 2));

    // SEO JSON
    const seoData = {
      metaTitle: `${brandName || companyName || "Travel"} - Flights, Hotels, Holidays`,
      metaDescription: `Book with ${brandName || companyName || "us"} for the best travel deals.`,
      metaKeywords: "Flights, Hotels, Holidays, Travel, Tours"
    };
    setSeoJson(JSON.stringify(seoData, null, 2));

    // Company Data JSON
    const companyData = {
    isSupplierAPI: agent?.isSupplierAPI || false,
    };
    setCompanyDataJson(JSON.stringify(companyData, null, 2));

    // Header JSON - Empty array
    setHeaderJson("[]");
    
    // Top Bar JSON - Empty object
    setTopBarJson("{}");
  };

  // ─── Handle Agent Selection ──────────────────────────────────────
  const handleAgentSelect = (agent) => {
    if (selectedAgent?.companyId === agent.companyId) {
      setSelectedAgent(null);
    } else {
      setSelectedAgent(agent);
      
      if (selectedTemplate) {
        const defaultDomain = `${agent.brandName || agent.companyName || "agent"}-${selectedTemplate.template || "site"}.zobookai.com`;
        setDomainName(defaultDomain.toLowerCase().replace(/\s+/g, "-"));
        
        // Update company data with agent info
        const companyData = {
         isSupplierAPI: agent.isSupplierAPI || false,
        };
        setCompanyDataJson(JSON.stringify(companyData, null, 2));

        // Update branding with agent info
        if (customBrandingJson) {
          try {
            const branding = JSON.parse(customBrandingJson);
            branding.companyName = agent.companyName || "";
            branding.brandName = agent.brandName || "";
            branding.email = agent.email || "";
            branding.phones = [agent.phoneNumber || ""];
            branding.whatsapp = agent.phoneNumber || "";
            setCustomBrandingJson(JSON.stringify(branding, null, 2));
          } catch (e) {
            console.error("Error updating branding JSON:", e);
          }
        }

        // Update about us with agent info
        if (aboutUsJson) {
          try {
            const aboutUs = JSON.parse(aboutUsJson);
            aboutUs.heroTitle = `About ${agent.brandName || agent.companyName || "Our Agency"}`;
            aboutUs.title = `Creating Memorable Journeys Across the World`;
            aboutUs.description = `${agent.brandName || agent.companyName} was founded with a passion for travel and a commitment to making every journey extraordinary.`;
            setAboutUsJson(JSON.stringify(aboutUs, null, 2));
          } catch (e) {
            console.error("Error updating about us JSON:", e);
          }
        }
      }
    }
    setAssignmentSuccess(false);
    setErrorMessage("");
  };

  // ─── Handle Logo Upload ───────────────────────────────────────────
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview("");
    // Update custom branding JSON
    if (customBrandingJson) {
      try {
        const branding = JSON.parse(customBrandingJson);
        branding.logoUrl = "";
        setCustomBrandingJson(JSON.stringify(branding, null, 2));
      } catch (e) {
        console.error("Error updating branding JSON:", e);
      }
    }
  };

  // ─── Handle Assignment Submission ─────────────────────────────────
  const handleAssignTemplate = async () => {
    if (!selectedAgent || !selectedTemplate) {
      setErrorMessage("Please select both an agent and a template.");
      return;
    }

    if (!domainName.trim()) {
      setErrorMessage("Please enter a domain name.");
      return;
    }

    setAssigning(true);
    setErrorMessage("");
    setAssignmentSuccess(false);

    try {
      // Prepare the DTO matching the backend method signature
      const formData = new FormData();
      
      // Add all required fields
      formData.append("CompanyId", selectedAgent.companyId);
      formData.append("TemplateId", selectedTemplate.id);
      formData.append("TemplateType", selectedTemplate.type);
      formData.append("BranchId", selectedAgent.branchId || "");
      formData.append("UserId", selectedAgent.userId || user?.uid || "");
      formData.append("TemplateReference", selectedTemplate.templateReference || selectedTemplate.template || "");
      formData.append("Domain", domainName.trim());
      
      // Add JSON data if available
      if (customBrandingJson) formData.append("CustomBrandingJson", customBrandingJson);
      if (styleJson) formData.append("StyleJson", styleJson);
      if (topBarJson) formData.append("TopBarJson", topBarJson);
      if (destinationsJson) formData.append("DestinationsJson", destinationsJson);
      if (heroJson) formData.append("HeroJson", heroJson);
      if (headerJson) formData.append("HeaderJson", headerJson);
      if (footerJson) formData.append("FooterJson", footerJson);
      if (aboutUsJson) formData.append("AboutUsJson", aboutUsJson);
      if (contactUsJson) formData.append("ContactUsJson", contactUsJson);
      if (faqJson) formData.append("FaqJson", faqJson);
      if (privacyPolicyJson) formData.append("PrivacyPolicyJson", privacyPolicyJson);
      if (termsConditionsJson) formData.append("TermsAndConditionsJson", termsConditionsJson);
      if (refundPolicyJson) formData.append("RefundPolicyJson", refundPolicyJson);
      if (seoJson) formData.append("SeoJson", seoJson);
      if (companyDataJson) formData.append("CompanyDataJson", companyDataJson);

      // Add logo file if uploaded
      if (logoFile) {
        formData.append("LogoFile", logoFile);
      }

      // Log the data being sent (for debugging)
      console.log("Sending assignment data:");
      for (let [key, value] of formData.entries()) {
        console.log(key, ":", value);
      }

      // Make API call using FormData for multipart/form-data
      const response = await apiService.post(
        config.ASSIGNTEMPLATE,
        formData
      );

      if (response && (response.success || response.status === "success" || response === true)) {
        setAssignmentSuccess(true);
        await fetchExistingAssignments();
        
        // Show success message
        setTimeout(() => {
          setSelectedAgent(null);
          setSelectedTemplate(null);
          setDomainName("");
          setLogoFile(null);
          setLogoPreview("");
          setAssignmentSuccess(false);
          resetCustomizationData();
        }, 3000);
      } else {
        setErrorMessage(
          response?.message || "Failed to assign template. Please try again."
        );
      }
    } catch (error) {
      console.error("Error assigning template:", error);
      setErrorMessage(
        error.response?.data?.message ||
          error.message ||
          "An error occurred while assigning the template."
      );
    } finally {
      setAssigning(false);
    }
  };

  // ─── Helper: Get Badge Color for Template Type ───────────────────
  const getTypeBadgeColor = (type) => {
    const colors = {
      Travel: "bg-info",
      B2C: "bg-success",
      B2E: "bg-primary",
      B2B: "bg-warning",
      Corporate: "bg-primary",
    };
    return colors[type] || "bg-secondary";
  };

  // ─── Helper: Get Icon for Template Type ──────────────────────────
  const getTypeIcon = (type) => {
    const icons = {
      Travel: faGlobe,
      B2C: faUsers,
      B2E: faBuilding,
      B2B: faBriefcase,
      Corporate: faBuilding,
    };
    return icons[type] || faFile;
  };

  // ─── JSON Editor Component ────────────────────────────────────────
  const JsonEditor = ({ label, value, onChange, helpText }) => (
    <div className="mb-3">
      <label className="form-label fw-bold">{label}</label>
      <textarea
        className="form-control"
        rows="4"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Enter ${label} JSON...`}
        style={{ fontFamily: "monospace", fontSize: "13px" }}
      />
      {helpText && <small className="text-muted">{helpText}</small>}
    </div>
  );

  // ─── Loading / Auth Guard Render ──────────────────────────────────
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) return null;

  // ─── Main Render ──────────────────────────────────────────────────
  return (
    <>
      <Header />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              {/* ─── ClientTabs Section (same as CompanyClients) ── */}
              <div className="p-2">
                <ClientTabs pageName="ASSIGNTEMPLATE" />
              </div>
              
              {/* ─── Card Body ── */}
              <div className="card-body">
                {/* ─── Page Header ── */}
                <div className="row mb-4">
                  <div className="col-12">
                    <h4 className="fw-bold text-primary">
                      <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
                      Assign Templates to Agents
                    </h4>
                    <p className="text-muted">
                      Select an agent and a template to assign. Customize the template settings and upload branding assets.
                    </p>
                  </div>
                </div>

                {/* ─── Assignment Summary ── */}
                <div className="row mb-4">
                  <div className="col-md-4">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <h5 className="text-muted">Agents</h5>
                        <h3 className="fw-bold">{agents.length}</h3>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <h5 className="text-muted">Templates</h5>
                        <h3 className="fw-bold">{templates.length}</h3>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ─── Two-Column Layout ── */}
                <div className="row g-4">
                  {/* ─── LEFT COLUMN: Agent List ── */}
                  <div className="col-md-5">
                    <div className="card border shadow-sm">
                      <div className="card-header bg-white py-3">
                        <h5 className="mb-0">
                          <FontAwesomeIcon icon={faUserCheck} className="me-2 text-primary" />
                          Select Agent
                        </h5>
                        <div className="mt-2">
                          <div className="input-group">
                            <span className="input-group-text bg-white">
                              <FontAwesomeIcon icon={faSearch} />
                            </span>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search agents..."
                              value={agentSearchTerm}
                              onChange={(e) => setAgentSearchTerm(e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="card-body p-0" style={{ maxHeight: "500px", overflowY: "auto" }}>
                        {agentsLoading ? (
                          <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status" />
                            <p className="mt-2 text-muted">Loading agents...</p>
                          </div>
                        ) : filteredAgents.length === 0 ? (
                          <div className="text-center py-5 text-muted">
                            <FontAwesomeIcon icon={faUser} className="fs-1 mb-2" />
                            <p>No agents found</p>
                          </div>
                        ) : (
                          <div className="list-group list-group-flush">
                            {filteredAgents.map((agent) => (
                              <div
                                key={agent.companyId}
                                className={`list-group-item list-group-item-action d-flex align-items-center p-3 ${
                                  selectedAgent?.companyId === agent.companyId
                                    ? "active bg-primary text-white"
                                    : ""
                                }`}
                                onClick={() => handleAgentSelect(agent)}
                                style={{ cursor: "pointer" }}
                              >
                                <div className="flex-grow-1">
                                  <h6 className="mb-0">{agent.companyName}</h6>
                                  <small
                                    className={
                                      selectedAgent?.companyId === agent.companyId
                                        ? "text-white-50"
                                        : "text-muted"
                                    }
                                  >
                                    {agent.brandName || agent.email}
                                    {agent.planType && (
                                      <span className="badge bg-info text-dark ms-2">
                                        {agent.planType}
                                      </span>
                                    )}
                                  </small>
                                </div>
                                {selectedAgent?.companyId === agent.companyId && (
                                  <FontAwesomeIcon icon={faCheckCircle} className="text-white" />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ─── RIGHT COLUMN: Template List ── */}
                  <div className="col-md-7">
                    <div className="card border shadow-sm">
                      <div className="card-header bg-white py-3">
                        <h5 className="mb-0">
                          <FontAwesomeIcon icon={faLayerGroup} className="me-2 text-primary" />
                          Select Template
                        </h5>
                        <div className="mt-2">
                          <div className="row g-2">
                            <div className="col-md-7">
                              <div className="input-group">
                                <span className="input-group-text bg-white">
                                  <FontAwesomeIcon icon={faSearch} />
                                </span>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search templates..."
                                  value={templateSearchTerm}
                                  onChange={(e) => setTemplateSearchTerm(e.target.value)}
                                />
                              </div>
                            </div>
                            <div className="col-md-5">
                              <select
                                className="form-select"
                                value={templateTypeFilter}
                                onChange={handleTypeFilterChange}
                              >
                                {templateTypeOptions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="card-body p-0" style={{ maxHeight: "500px", overflowY: "auto" }}>
                        {templatesLoading ? (
                          <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status" />
                            <p className="mt-2 text-muted">Loading templates...</p>
                          </div>
                        ) : filteredTemplates.length === 0 ? (
                          <div className="text-center py-5 text-muted">
                            <FontAwesomeIcon icon={faFile} className="fs-1 mb-2" />
                            <p>No templates found</p>
                          </div>
                        ) : (
                          <div className="list-group list-group-flush">
                            {filteredTemplates.map((template) => {
                              const isAssigned = !!existingAssignments[template.id];
                              const isSelected = selectedTemplate?.id === template.id;
                              
                              return (
                                <div
                                  key={template.id}
                                  className={`list-group-item list-group-item-action p-3 ${
                                    isSelected ? "active bg-primary text-white" : ""
                                  }`}
                                  onClick={() => handleTemplateSelect(template)}
                                  style={{ cursor: "pointer" }}
                                >
                                  <div className="d-flex align-items-center">
                                    {template.thumbnail ? (
                                      <img
                                        src={template.thumbnail}
                                        alt={template.title}
                                        className="rounded me-3"
                                        style={{
                                          width: "60px",
                                          height: "45px",
                                          objectFit: "cover",
                                        }}
                                      />
                                    ) : (
                                      <div
                                        className="rounded me-3 d-flex align-items-center justify-content-center bg-light"
                                        style={{ width: "60px", height: "45px" }}
                                      >
                                        <FontAwesomeIcon
                                          icon={getTypeIcon(template.type)}
                                          className="text-secondary"
                                        />
                                      </div>
                                    )}
                                    <div className="flex-grow-1">
                                      <h6 className="mb-0">{template.title}</h6>
                                      <small
                                        className={
                                          isSelected ? "text-white-50" : "text-muted"
                                        }
                                      >
                                        {template.description?.substring(0, 60)}
                                        {template.description?.length > 60 && "..."}
                                      </small>
                                      <div className="mt-1">
                                        <span
                                          className={`badge ${getTypeBadgeColor(
                                            template.type
                                          )} me-1`}
                                        >
                                          {template.type || "General"}-
                                          {template.template}
                                        </span>
                                        {isAssigned && (
                                          <span className="badge bg-success">
                                            <FontAwesomeIcon
                                              icon={faCheckCircle}
                                              className="me-1"
                                            />
                                            Assigned
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    {isSelected && (
                                      <FontAwesomeIcon
                                        icon={faCheckCircle}
                                        className="text-white ms-2"
                                      />
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ─── Assignment Form ── */}
                {selectedAgent && selectedTemplate && (
                  <div className="row mt-4">
                    <div className="col-12">
                      <div className="card border shadow-sm">
                        <div className="card-body">
                          <h5 className="fw-bold mb-3">
                            <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
                            Assign Template
                          </h5>

                          {/* ─── Basic Information ── */}
                          <div className="row g-3 mb-4">
                            <div className="col-md-4">
                              <label className="form-label fw-bold">Agent</label>
                              <div className="p-2 bg-light rounded">
                                <FontAwesomeIcon icon={faUserCheck} className="me-2 text-primary" />
                                <strong>{selectedAgent.companyName}</strong>
                                <br />
                                <small className="text-muted">
                                  {selectedAgent.email}
                                </small>
                              </div>
                            </div>

                            <div className="col-md-4">
                              <label className="form-label fw-bold">Template</label>
                              <div className="p-2 bg-light rounded">
                                <FontAwesomeIcon icon={faLayerGroup} className="me-2 text-primary" />
                                <strong>{selectedTemplate.title}</strong>
                                <br />
                                <span className={`badge ${getTypeBadgeColor(selectedTemplate.type)}`}>
                                  {selectedTemplate.type} - {selectedTemplate.template}
                                </span>
                              </div>
                            </div>

                            <div className="col-md-4">
                              <label className="form-label fw-bold">
                                Domain Name <span className="text-danger">*</span>
                              </label>
                              <div className="input-group">
                                <input
                                  type="text"
                                  className={`form-control ${errorMessage && !domainName.trim() ? "is-invalid" : ""}`}
                                  placeholder="Enter domain (e.g., agent-site.zobookai.com)"
                                  value={domainName}
                                  onChange={(e) => setDomainName(e.target.value)}
                                />
                                {errorMessage && !domainName.trim() && (
                                  <div className="invalid-feedback">{errorMessage}</div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* ─── Logo Upload ── */}
                          {/* <div className="mb-4">
                            <label className="form-label fw-bold">Logo</label>
                            <div className="row g-3">
                              <div className="col-md-6">
                                <div className="border rounded p-3 text-center">
                                  {logoPreview ? (
                                    <div className="position-relative">
                                      <img
                                        src={logoPreview}
                                        alt="Logo preview"
                                        className="img-fluid"
                                        style={{ maxHeight: "100px" }}
                                      />
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger position-absolute top-0 end-0"
                                        onClick={removeLogo}
                                      >
                                        <FontAwesomeIcon icon={faTimes} />
                                      </button>
                                    </div>
                                  ) : (
                                    <>
                                      <FontAwesomeIcon icon={faUpload} className="fs-2 text-muted mb-2" />
                                      <p className="text-muted mb-2">Upload logo image</p>
                                      <input
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={handleLogoUpload}
                                      />
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="col-md-6">
                                <small className="text-muted">
                                  Upload a logo image (PNG, JPG, SVG). Recommended size: 200x60px.
                                </small>
                              </div>
                            </div>
                          </div> */}

                          {/* ─── Customization JSONs ── */}
                          {/* <div className="mb-4">
                            <h6 className="fw-bold mb-3">Template Customization</h6>
                            <div className="row">
                              <div className="col-md-6">
                                <JsonEditor
                                  label="Branding"
                                  value={customBrandingJson}
                                  onChange={setCustomBrandingJson}
                                  helpText="Customize colors, fonts, and branding elements"
                                />
                                <JsonEditor
                                  label="About Us"
                                  value={aboutUsJson}
                                  onChange={setAboutUsJson}
                                  helpText="Configure about section content"
                                />
                                <JsonEditor
                                  label="Contact Us"
                                  value={contactUsJson}
                                  onChange={setContactUsJson}
                                  helpText="Configure contact form and information"
                                />
                                <JsonEditor
                                  label="FAQ"
                                  value={faqJson}
                                  onChange={setFaqJson}
                                  helpText="Configure frequently asked questions"
                                />
                                <JsonEditor
                                  label="Privacy Policy"
                                  value={privacyPolicyJson}
                                  onChange={setPrivacyPolicyJson}
                                  helpText="Configure privacy policy content"
                                />
                                <JsonEditor
                                  label="Terms & Conditions"
                                  value={termsConditionsJson}
                                  onChange={setTermsConditionsJson}
                                  helpText="Configure terms and conditions content"
                                />
                              </div>
                              <div className="col-md-6">
                                <JsonEditor
                                  label="Refund Policy"
                                  value={refundPolicyJson}
                                  onChange={setRefundPolicyJson}
                                  helpText="Configure refund and cancellation policy"
                                />
                                <JsonEditor
                                  label="Hero Section"
                                  value={heroJson}
                                  onChange={setHeroJson}
                                  helpText="Configure hero banner, title, subtitle, and CTA"
                                />
                                <JsonEditor
                                  label="Destinations"
                                  value={destinationsJson}
                                  onChange={setDestinationsJson}
                                  helpText="Configure destinations and travel packages"
                                />
                                <JsonEditor
                                  label="Header"
                                  value={headerJson}
                                  onChange={setHeaderJson}
                                  helpText="Configure navigation menu and header elements"
                                />
                                <JsonEditor
                                  label="Footer"
                                  value={footerJson}
                                  onChange={setFooterJson}
                                  helpText="Configure footer links, social media, and copyright"
                                />
                                <JsonEditor
                                  label="SEO"
                                  value={seoJson}
                                  onChange={setSeoJson}
                                  helpText="Configure meta tags, title, description, and keywords"
                                />
                                <JsonEditor
                                  label="Company Data"
                                  value={companyDataJson}
                                  onChange={setCompanyDataJson}
                                  helpText="Company information including name, address, contact details"
                                />
                                <JsonEditor
                                  label="Style"
                                  value={styleJson}
                                  onChange={setStyleJson}
                                  helpText="Configure theme colors and styling"
                                />
                              </div>
                            </div>
                          </div> */}

                          {/* ─── Action Buttons ── */}
                          <div className="mt-4 d-flex gap-2">
                            <button
                              className="btn btn-success px-4"
                              onClick={handleAssignTemplate}
                              disabled={assigning || assignmentSuccess}
                            >
                              {assigning ? (
                                <>
                                  <FontAwesomeIcon icon={faSpinner} className="fa-spin me-2" />
                                  Assigning...
                                </>
                              ) : assignmentSuccess ? (
                                <>
                                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                  Assigned Successfully!
                                </>
                              ) : (
                                <>
                                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                  Assign Template
                                </>
                              )}
                            </button>
                            <button
                              className="btn btn-danger px-4"
                              onClick={() => {
                                setSelectedAgent(null);
                                setSelectedTemplate(null);
                                setDomainName("");
                                setAssignmentSuccess(false);
                                setErrorMessage("");
                                resetCustomizationData();
                                setLogoFile(null);
                                setLogoPreview("");
                              }}
                              disabled={assigning}
                            >
                              Clear Selection
                            </button>
                          </div>

                          {errorMessage && (
                            <div className="mt-3 alert alert-danger">
                              <FontAwesomeIcon icon={faSpinner} className="me-2" />
                              {errorMessage}
                            </div>
                          )}

                          {assignmentSuccess && (
                            <div className="mt-3 alert alert-success">
                              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                              Template assigned successfully to {selectedAgent.companyName}!
                              {existingAssignments[selectedTemplate.id] && (
                                <span className="ms-2">
                                  (Domain: {existingAssignments[selectedTemplate.id].domain})
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ─── Info Message ── */}
                {!selectedAgent && !selectedTemplate && (
                  <div className="row mt-4">
                    <div className="col-12">
                      <div className="alert alert-info text-center">
                        <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
                        Please select an agent from the left and a template from the right to begin assignment.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssignTemplateToAgent;